import { Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { applicationService } from "../../../../shared/api/applicationService";
import { eventService } from "../../../../shared/api/event/eventService";
import {
	DeepApplicationFormValues,
	KronosApplicationFormValues,
	ParticipantManagementApplicationFormValues,
} from "../../../../shared/types/application";

export const useCreateApplication = (eventId: Id) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const handleSuccessfulApplicationCreate = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: eventService.listKey });
		void queryClient.invalidateQueries({ queryKey: eventService.showKey(eventId) });
		void queryClient.invalidateQueries({ queryKey: applicationService.keys.list });
		showToast({
			type: "success",
			title: t("Application"),
			body: t("Application was successfully created."),
		});
	}, [eventId, queryClient, showToast, t]);

	const createKronosApplication = useCallback(
		async (values: KronosApplicationFormValues) => {
			const response = await applicationService.storeKronos({ ...values, eventId });

			if (isSuccess(response)) {
				handleSuccessfulApplicationCreate();
				return true;
			}
			return false;
		},
		[eventId, handleSuccessfulApplicationCreate],
	);

	const createDeepApplication = useCallback(
		async (values: DeepApplicationFormValues) => {
			const response = await applicationService.storeDeep({ ...values, eventId });

			if (isSuccess(response)) {
				handleSuccessfulApplicationCreate();
				return true;
			}
			return false;
		},
		[eventId, handleSuccessfulApplicationCreate],
	);

	const createParticipantManagementApplication = useCallback(
		async (values: ParticipantManagementApplicationFormValues) => {
			const response = await applicationService.storeParticipantManagement({ ...values, eventId });

			if (isSuccess(response)) {
				handleSuccessfulApplicationCreate();
				return true;
			}
			return false;
		},
		[eventId, handleSuccessfulApplicationCreate],
	);

	return { createKronosApplication, createDeepApplication, createParticipantManagementApplication };
};
